import { useEffect } from "react";
import {
  useCurrentUser,
  useEvmAddress,
  useEvmSmartAccounts,
  useGetAccessToken,
  useIsInitialized,
  useIsSignedIn,
  useSignOut,
} from "@coinbase/cdp-hooks";
import { registerAccessTokenGetter } from "./accessTokenBridge";

export interface CdpAuthState {
  /** False until the CDP SDK has finished restoring/initializing a session. */
  isInitialized: boolean;
  isSignedIn: boolean;
  /** Stable Coinbase end-user ID, if signed in. */
  cdpUserId: string | null;
  email: string | null;
  /** The Smart Account address used for checkout/escrow calls. Null until provisioned. */
  smartAccountAddress: `0x${string}` | null;
  /** The underlying EOA that owns the Smart Account, if present. */
  eoaAddress: `0x${string}` | null;
  signOut: () => Promise<void>;
}

/**
 * Adapter-shaped view over the raw CDP hooks: this is the surface the rest of the app
 * should consume instead of importing `@coinbase/cdp-hooks` directly, so a future API
 * change is a one-file fix. Also registers the access-token getter used by the plain
 * (non-React) API client — see `accessTokenBridge.ts`.
 */
export function useCdpAuth(): CdpAuthState {
  const { isInitialized } = useIsInitialized();
  const { isSignedIn } = useIsSignedIn();
  const { currentUser } = useCurrentUser();
  const { evmAddress } = useEvmAddress();
  const { evmSmartAccounts } = useEvmSmartAccounts();
  const { signOut } = useSignOut();
  const { getAccessToken } = useGetAccessToken();

  // `useAuthPrincipal()` (and therefore this hook) is called from several places at once
  // (AppShell, CheckoutPage, OrderDetailPage, ...), each independently mounting/unmounting
  // as routes change — but `registerAccessTokenGetter` writes to a single module-level
  // singleton shared by all of them (see `accessTokenBridge.ts`'s docblock for why). An
  // unconditional cleanup that nulls the getter on *every* unmount races against sibling
  // instances: e.g. navigating from checkout to the order page can unmount checkout's
  // instance (nulling the getter) around the same time the order page's instance mounts
  // (re-registering it), leaving a real window where an in-flight request finds no token
  // and gets a spurious 401. Every mounted instance derives the getter from the same
  // underlying CDP session, so they're redundant, not conflicting — only null it out when
  // *this* instance's own signed-out state says to, never on unmount.
  useEffect(() => {
    registerAccessTokenGetter(isSignedIn ? getAccessToken : null);
  }, [isSignedIn, getAccessToken]);

  const smartAccountAddress = (evmSmartAccounts?.[0]?.address as `0x${string}` | undefined) ?? null;
  // useEvmAddress() prioritizes Smart Account > EOA; when a smart account exists we want
  // the *owning* EOA separately (e.g. for display), so fall back to evmAddress only when
  // there is no smart account (meaning evmAddress is itself the EOA).
  const eoaAddress = smartAccountAddress ? null : (evmAddress ?? null);

  return {
    isInitialized,
    isSignedIn,
    cdpUserId: currentUser?.userId ?? null,
    email: currentUser?.authenticationMethods?.email?.email ?? null,
    smartAccountAddress,
    eoaAddress,
    signOut,
  };
}
